import { BANNER_HEIGHT } from '@modules/infoBanner/constants';
import { useIsInfoBannerVisible } from '@modules/infoBanner/hooks';
import { WISDOM_CHAT_LIST_DRAWER_WIDTH } from '@modules/oracle/constants';
import { AddRounded, ChevronLeftRounded } from '@mui/icons-material';
import { Box, Drawer, Stack, SxProps, Theme } from '@mui/material';
import { CreateNewChatButton } from '../CreateNewChatButton';
import { WisdomChatListDrawerToggleButton } from '../WisdomChatListDrawerToggleButton';
import { WisdomSavedChatList } from '../WisdomSavedChatList';

interface WisdomSavedChatsListDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const startTransitionStyles: SxProps<Theme> = {
  width: WISDOM_CHAT_LIST_DRAWER_WIDTH,
  transition: (theme) =>
    theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  overflow: 'visible',
};

const leaveTransitionStyles: SxProps<Theme> = {
  transition: (theme) =>
    theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  width: 0,
  overflow: 'hidden',
};

export const WisdomSavedChatsListDrawer = ({
  isOpen,
  onClose,
}: WisdomSavedChatsListDrawerProps) => {
  const isInfoBannerVisible = useIsInfoBannerVisible();

  const drawerTransitionStyles = isOpen
    ? startTransitionStyles
    : leaveTransitionStyles;

  return (
    <Drawer
      sx={{
        ...drawerTransitionStyles,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          left: 86, // same as nav-bar width
          top: isInfoBannerVisible ? BANNER_HEIGHT : 0,
          height: isInfoBannerVisible
            ? `calc(100% - ${BANNER_HEIGHT}px)`
            : undefined,
          ...drawerTransitionStyles,
        },
        zIndex: (theme) => theme.zIndex.drawer - 1,
        position: 'relative',
      }}
      variant="persistent"
      anchor="left"
      open={isOpen}
    >
      <Stack sx={{ p: 1.5, pt: 0, pb: 0, height: '100%', overflowY: 'hidden' }}>
        <Box
          sx={{
            pt: 3,
            position: 'sticky',
            top: 0,
            zIndex: (theme) => theme.zIndex.drawer,
            backgroundColor: (theme) => theme.palette.misc?.drawer.background,
          }}
        >
          <CreateNewChatButton
            buttonProps={{
              variant: 'outlined',
              startIcon: <AddRounded />,
              sx: {
                textAlign: 'left',
              },
              fullWidth: true,
            }}
            buttonText="New Chat"
          />
        </Box>
        <Stack direction="column" height="100%" flex={1}>
          <WisdomSavedChatList />
        </Stack>
      </Stack>
      <Box
        position="absolute"
        top="50%"
        right={-8}
        sx={{ zIndex: (theme) => theme.zIndex.drawer }}
      >
        <WisdomChatListDrawerToggleButton
          icon={
            <ChevronLeftRounded
              sx={{ color: (theme) => theme.palette.primary.contrastText }}
            />
          }
          onClick={onClose}
        />
      </Box>
    </Drawer>
  );
};
